/**
 * Storage OSS Information Column Configuration
 *
 * Shows rows per configoption (storage types).
 */
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import {
    createCurrencyFormatter,
    createDateFormatter,
    createTimeRangeFormatter,
} from '@/components/ui/data-table-customized'
import type { TFunction } from 'i18next'
import { formatNumberUsage } from '@/utils/function'
import { Service, ServiceConfigoption } from '@/modules/Service/types'
import ServiceContract from '../../../shared/ServiceContract'
import { Checkbox } from '@/components/ui/checkbox'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'

// Row type for Storage OSS table
export type StorageOSSRow = ServiceConfigoption &
    Partial<Service> & { entity_name?: string }

// ============================================================================
// Column Configuration
// ============================================================================

export const createStorageOSSColumns = (
    t: TFunction,
    hasPrice: boolean = false
): DataTableColumn<StorageOSSRow>[] => [
    {
        key: 'entity_name',
        title: t('entity-name'),
        width: 200,
        pinned: 'left',
        render: (value) => value || '—',
    },
    {
        key: 'service_name',
        title: 'OSS',
        width: 200,
        pinned: 'left',
        render: (value) => value?.toString()?.toUpperCase() || '-',
    },
    {
        key: 'commitment_value',
        title: t('commitment_value'),
        width: 150,
        cellClassName: 'text-right',
        render: (value, row) => {
            if (!value) {
                return (
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="text-muted-foreground text-xs">
                                    PAYG
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{t('payg')}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )
            }
            const commitmentTime = row?.commitment_time
            if (commitmentTime && commitmentTime > 1) {
                return `${formatNumberUsage(value)}GB/ ${t('month')}`
            }
            return formatNumberUsage(value)
        },
    },
    {
        key: 'commitment_prepaid',
        title: t('commitment_prepaid.true'),
        width: 100,
        hidden: !hasPrice,
        render: (value, row) => {
            if (!row?.commitment_value || row.commitment_value <= 0) return null
            return (
                <div className="flex justify-center">
                    <Checkbox checked={!!value} disabled />
                </div>
            )
        },
    },
    {
        key: 'commitment_price_per_unit',
        title: t('commitment_price_per_unit'),
        width: 120,
        cellClassName: 'text-right',
        render: (value, row) => {
            const price =
                value > 0 ? value : row?.price_ranges?.[0]?.price_per_unit
            if (!price) return '-'
            return undefined
        },
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'commitment_price_total',
        title: t('commitment_price_total'),
        width: 130,
        cellClassName: 'text-right',
        hidden: !hasPrice,
        render: (value) => {
            if (!value || value <= 0) return '-'
            return undefined
        },
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'employee_names',
        title: t('employee_sale_tracking_ids'),
        width: 200,
        render: (_, row) => row?.employee_names?.join(', ') || '-',
    },
    {
        key: 'service_times.commitment',
        title: t('service_times.commitment'),
        width: 180,
        cellClassName: 'text-right',
        hidden: !hasPrice,
        render: (_, row) => {
            if (!row?.commitment_value || row.commitment_value <= 0) return null
            return undefined
        },
        formatter: createTimeRangeFormatter({
            startKey: 'commitment_startdate',
            endKey: 'commitment_enddate',
            format: 'DD/MM/YYYY',
            smartYear: true,
        }),
    },
    {
        key: 'service_start_date',
        title: t('service_start_date'),
        width: 110,
        cellClassName: 'text-right',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'service_stopped_date',
        title: t('service_stopped_date'),
        width: 110,
        cellClassName: 'text-right',
        hidden: true,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'service_contract',
        title: t('service_contract'),
        width: 300,
        render: (_, row) => {
            if (!row?.service_id) return null
            return <ServiceContract serviceID={row.service_id} />
        },
    },
]

// ============================================================================
// Data Transformer
// ============================================================================

interface TransformOptions {
    service: Service
    data: ServiceConfigoption[]
}

export const transformStorageOSSData = ({
    service,
    data,
}: TransformOptions): StorageOSSRow[] => {
    if (!data?.length) return []

    return data.map((configoption) => ({
        ...configoption,
        entity_name: service?.name,
        service_id: service?.service_id,
        service_name: service?.service_name,
        service_start_date: service?.service_start_date,
        service_stopped_date: service?.service_stopped_date,
        service_status: service?.service_status,
        employee_names: service?.employee_names,
    }))
}
